import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import Messenger from '../components/Messenger'

/**
 * Переписка с клиентами.
 *
 * Тот же экран, что у клиента в кабинете, только со стороны: слева беседы всех
 * клиентов, которых человек видит в программе, справа разговор. Отдельной
 * области видимости для переписки нет — она следует за клиентами, иначе два
 * правила разошлись бы.
 */
export default function Chat() {
  const qc = useQueryClient()
  const { can } = useAuth()
  const nav = useNavigate()
  const [openedId, setOpenedId] = useState<number | null>(null)
  const [error, setError] = useState('')

  const list = useQuery({
    queryKey: ['conversations'],
    queryFn: api.conversations,
    // Клиент мог написать минуту назад — список обновляется сам
    refetchInterval: 20_000,
  })

  const opened = useQuery({
    queryKey: ['conversation', openedId],
    queryFn: () => api.conversation(openedId!),
    enabled: openedId !== null,
    refetchInterval: 20_000,
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['conversations'] })
    qc.invalidateQueries({ queryKey: ['conversation'] })
    qc.invalidateQueries({ queryKey: ['chat-unread'] })
    // Переписка обращения — та же самая: карточка должна это увидеть
    qc.invalidateQueries({ queryKey: ['tickets'] })
  }

  async function send(
    body: string,
    file: File | null,
    opts: { replyTo: number | null; forwardOf: number | null },
  ) {
    if (openedId === null) return
    setError('')
    try {
      await api.sendChat(openedId, body, file, opts)
      refresh()
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось отправить')
    }
  }

  async function edit(messageId: number, body: string) {
    if (openedId === null) return
    setError('')
    try {
      await api.editChatMessage(openedId, messageId, body)
      refresh()
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось изменить')
    }
  }

  const rows = list.data ?? []
  const ждут = rows.filter((c) => c.unread > 0).length

  return (
    <>
      <h1 className="page-title">Сообщения</h1>

      <Messenger
        conversations={rows}
        loading={list.isLoading}
        opened={opened.data ?? null}
        openedId={openedId}
        onOpen={setOpenedId}
        onSend={can('tickets.reply') ? send : async () => setError('Нет права отвечать клиентам')}
        onEdit={can('tickets.reply') ? edit : async () => setError('Нет права отвечать клиентам')}
        onOpenTicket={(id, kind) =>
          nav(`${kind === 'order' ? '/orders' : '/tickets'}?open=${id}`)
        }
        onDownload={(messageId, name) => {
          if (openedId === null) return
          void api
            .downloadChatFile(openedId, messageId, name)
            .catch((e: any) => setError(e?.message ?? 'Не удалось скачать файл'))
        }}
        mineIsClient={false}
        error={error}
        emptyHint="Переписки пока нет"
        header={
          <div className="ms-start">
            <div className="ms-start-title">
              Беседы{ждут > 0 && <span className="ms-badge">{ждут}</span>}
            </div>
            <p className="hint" style={{ margin: 0 }}>
              Разговор начинает клиент — из кабинета. Здесь видно всё, что он
              написал: и по обращениям, и напрямую.
            </p>
          </div>
        }
      />
    </>
  )
}
